import { formatDateTime } from '@fastlessjs/backoffice-layer/dist/utils/dateFormat';

import { t } from '../../config/translations';

export type EnterpriseResolver = (id?: number | null) => string;

const defaultResolver: EnterpriseResolver = (id) => (id != null ? `#${id}` : '—');

/**
 * Lightweight schema used by the dashboard Orders listing tab.
 * Only fields returned by /stats/orders are declared.
 * `decorators.search: true` is required for Listing to render the column.
 */
export function createDashboardOrderSchema(
  resolveEnterprise: EnterpriseResolver = defaultResolver,
) {
  return {
    title: 'Orders',
    type: 'object',
    properties: {
      _createdAt: {
        type: 'string',
        get title() {
          return t('schema.createdAt');
        },
        decorators: {
          search: true,
          action: (v: string) => formatDateTime(v) || '—',
        },
      },
      orderId: {
        type: 'string',
        get title() {
          return t('schema.orderId');
        },
        decorators: { search: true, searchable: true, copy: true },
      },
      extOrderId: {
        type: 'string',
        get title() {
          return t('schema.extOrderId');
        },
        decorators: { search: true, searchable: true },
      },
      enterpriseId: {
        type: 'number',
        get title() {
          return t('schema.enterprise');
        },
        decorators: {
          search: true,
          searchable: true,
          action: (id: number) => resolveEnterprise(id),
        },
      },
      clientEmail: {
        type: 'string',
        get title() {
          return t('schema.clientEmail');
        },
        decorators: { search: true, searchable: true },
      },
      clientRef: {
        type: 'string',
        get title() {
          return t('schema.clientReference');
        },
        decorators: { search: true, searchable: true },
      },
      orderDate: {
        type: 'string',
        get title() {
          return t('schema.orderDate');
        },
        decorators: { search: true },
      },
    },
    required: [],
  };
}

/**
 * Lightweight schema used by the dashboard Returns (Suivi) listing tab.
 */
export function createDashboardSuiviSchema(
  resolveEnterprise: EnterpriseResolver = defaultResolver,
) {
  return {
    title: 'Returns',
    type: 'object',
    properties: {
      _createdAt: {
        type: 'string',
        get title() {
          return t('schema.createdAt');
        },
        decorators: {
          search: true,
          action: (v: string) => formatDateTime(v) || '—',
        },
      },
      suiviId: {
        type: 'string',
        get title() {
          return t('schema.returnId');
        },
        decorators: { search: true, searchable: true, copy: true },
      },
      orderId: {
        type: 'string',
        get title() {
          return t('schema.orderId');
        },
        decorators: { search: true, searchable: true },
      },
      enterpriseId: {
        type: 'number',
        get title() {
          return t('schema.enterprise');
        },
        decorators: {
          search: true,
          searchable: true,
          action: (id: number) => resolveEnterprise(id),
        },
      },
      clientUserName: {
        type: 'string',
        get title() {
          return t('schema.clientName');
        },
        decorators: { search: true, searchable: true },
      },
      clientUserEmail: {
        type: 'string',
        get title() {
          return t('schema.clientEmail');
        },
        decorators: { search: true, searchable: true },
      },
      createdByRole: {
        type: 'string',
        get title() {
          return t('schema.creatorRole');
        },
        decorators: { search: true, searchable: true },
      },
      eurekaStatus: {
        type: 'string',
        get title() {
          return t('schema.eurekaStatus');
        },
        decorators: { search: true, searchable: true },
      },
    },
    required: [],
  };
}

export const dashboardOrderSchema = createDashboardOrderSchema();
export const dashboardSuiviSchema = createDashboardSuiviSchema();
